// The trameria desktop app's bridge (`window.trameriaDesktop`), as the screen
// uses it: only the members Me needs, typed here because the package that
// defines the whole bridge is not part of the offline repository. Both desktop
// apps (cloud's and the offline edition's) expose it; in a browser the screen
// falls back to the browser's APIs.
//
// Promise: `desktopShell()` is null outside the desktop app. Members a desktop
// of an older version may lack are optional and called with `?.`.
//
// A page the desktop app's screen frames (the cloud's, in an iframe) has no
// bridge of its own; the app hands it one for its notices only
// (`provideNoticeDesktop`). `noticeDesktop()` is the one that shows this
// screen's notices: the app's own bridge, or else the one handed over.
import { useSyncExternalStore } from "react";

export type DesktopNotificationStatus = "ok" | "blocked" | "unavailable";

export interface DesktopShellNotice {
  title: string;
  body: string;
  /** Where a click goes: a path on the app's origin. */
  url: string;
  tag: string;
  /** Absent: the app's badge stays as it is. */
  badge?: number;
  /** Buttons where the app shows them (mac): each goes to its own path. */
  actions?: { id: string; label: string; url: string }[];
}

/** The Block the desktop app's menu bar shows (the running one, else the next). */
export interface DesktopMenuBarBlock {
  id: string;
  title: string;
  state: "next" | "running";
  /** ISO 8601. */
  startAt: string;
  endAt: string;
  startedAt: string | null;
  items: {
    caseId: string;
    /** null: the whole Case is linked. */
    stepId: string | null;
    title: string;
    caseTitle: string;
    done: boolean;
    /** Being worked on now inside the Block (switched by hand). */
    working: boolean;
  }[];
}

/** A click on the menu bar's Block; the screen calls its API for it. */
export type DesktopMenuBarBlockAction =
  | { type: "block-start" | "block-end"; id: string }
  | { type: "block-step-done"; caseId: string; stepId: string };

export interface DesktopShell {
  installationId: string;
  /** The Block the menu bar shows (null: none). Absent where the app has no menu bar. */
  setMenuBarBlock?(block: DesktopMenuBarBlock | null): void;
  onMenuBarBlockAction?(
    handler: (action: DesktopMenuBarBlockAction) => void,
  ): () => void;
  notify(message: DesktopShellNotice): void;
  closeNotifications?(tag: string): void;
  setBadge?(count: number): void;
  onNavigate?(handler: (url: string) => void): () => void;
  /** Whether the window is in front and being looked at. */
  isActive?(): boolean;
  onActiveChange?(handler: (active: boolean) => void): () => void;
  notificationStatus?(): DesktopNotificationStatus;
  onNotificationStatusChange?(
    handler: (status: DesktopNotificationStatus) => void,
  ): () => void;
  /** Tells the app whether delivery to it is on (it then checks the mac permission). */
  setNotificationTarget?(on: boolean): void;
  openNotificationSettings?(): void;
}

export function desktopShell(): DesktopShell | null {
  if (typeof window === "undefined") return null;
  const bridge = (window as unknown as { trameriaDesktop?: DesktopShell })
    .trameriaDesktop;
  return bridge && typeof bridge.installationId === "string" ? bridge : null;
}

/** The desktop handed to a framed page for its notices (null: none). */
let handed: DesktopShell | null = null;
const handedListeners = new Set<() => void>();

/** Set by a page the desktop app's screen frames, once the app said who it is. */
export function provideNoticeDesktop(shell: DesktopShell | null): void {
  handed = shell;
  for (const fn of handedListeners) fn();
}

/** The desktop app that shows this screen's notices, or null (a browser). */
export function noticeDesktop(): DesktopShell | null {
  return desktopShell() ?? handed;
}

const subscribeHanded = (fn: () => void) => {
  handedListeners.add(fn);
  return () => void handedListeners.delete(fn);
};

/** `noticeDesktop()`, drawn again when one is handed over. */
export function useNoticeDesktop(): DesktopShell | null {
  return useSyncExternalStore(subscribeHanded, noticeDesktop, () => null);
}
